import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner } from '@blueprintjs/core';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProgressDialog from 'components/ProgressDialog';
import { RootState } from 'data/root.reducers';
import * as radiusParserPasswordSelectors from 'data/radiusParserPassword/radiusParserPassword.selectors';
import * as radiusParserPasswordActions from 'data/radiusParserPassword/radiusParserPassword.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  RadiusParserPassword,
} from 'data/radiusParserPassword/radiusParserPassword.types';
import RadiusParserPasswordForm from '../../components/RadiusParserPasswordForm';
import { Ref } from '../../components/RadiusParserPasswordForm/RadiusParserPasswordForm';
import { radiusParserPasswordTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateFieldChanges
} from '../../utils/radiusParserPasswordTemplate.adapter';
import {
  getAuditDetailsToSubmit
} from '../../utils/radiusParserPasswordDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  radiusParserPassword: radiusParserPasswordSelectors.getRadiusParserPassword(state),
  radiusParserPasswordAdaptedTemplate: adaptTemplateVisibilityMode(radiusParserPasswordTemplate, 'create'),
  radiusUiState: radiusParserPasswordSelectors.getRadiusParserPasswordsUIState(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
});

const mapDispatch = {
  goToRadiusParserPasswordView: radiusParserPasswordActions.goToRadiusParserPasswordView,
  getRadiusParserPassword: radiusParserPasswordActions.getRadiusParserPassword,
  createRadiusParserPassword: radiusParserPasswordActions.createRadiusParserPassword,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RadiusParserPasswordCreate = ({
  history,
  radiusParserPassword,
  radiusParserPasswordAdaptedTemplate,
  isAuditEnabled,
  userName,
  userId,
  radiusUiState,
  getRadiusParserPassword,
  goToRadiusParserPasswordView,
  createRadiusParserPassword,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<Partial<RadiusParserPassword>>({});
  const [template, setTemplate] = useState<FormTemplate>(radiusParserPasswordAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(radiusUiState.isLoading);
  }, [radiusUiState]);

  useEffect(() => {
    if (!radiusParserPassword?.id) {
      getRadiusParserPassword();
    }
  }, [radiusParserPassword, getRadiusParserPassword]);

  useEffect(() => {
    if (radiusParserPassword?.id) {
      goToRadiusParserPasswordView(history)
    }
  }, [history, radiusParserPassword, goToRadiusParserPasswordView]);

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const radiusParserPasswordData = formRef.current.getValues();

    const payload = {
      data: {
        id: '',
        module: radiusParserPasswordData.module,
        usage: radiusParserPasswordData.usage,
        password: radiusParserPasswordData.password,
        trclvl: radiusParserPasswordData.trclvl,
      } as RadiusParserPassword,
      auditDetails: getAuditDetailsToSubmit(
        radiusParserPasswordData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    createRadiusParserPassword(payload, history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {

    setTemplate(adaptTemplateFieldChanges(template, fieldName, value));

    const newFormData = cloneDeep(formData as RadiusParserPassword);

    setFormData({
      ...newFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (template) {
      return (
        <RadiusParserPasswordForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('radiusParserPassword.create.title')}>
        <FormButtons
          isEditing
          onClickSave={handleClickSave}
        />
      </FormPanel>
    );
  };

  if (loading && !radiusUiState.isSaving) {
    return <Spinner aria-label="Loading..." />;
  }

  return (
    <div data-testid="RadiusParserPasswordCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ProgressDialog
        open={radiusUiState.isSaving}
        title={t('radiusParserPassword.edit.saving')}
      />
    </div>
  );
};

export default withRouter(connector(RadiusParserPasswordCreate));
